import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import {
  type PersistedState,
  defaultPersistedState,
  migrateState
} from "@aus-property/core";
import {
  SQLiteConnection,
  CapacitorSQLite,
  type SQLiteDBConnection
} from "@capacitor-community/sqlite";

const STORAGE_KEY = "aus_property_state";

type SerializedState = string;

interface StateStorage {
  loadSerialized(): Promise<SerializedState | null>;
  saveSerialized(value: SerializedState): Promise<void>;
}

class PreferencesStorage implements StateStorage {
  async loadSerialized(): Promise<SerializedState | null> {
    const result = await Preferences.get({ key: STORAGE_KEY });
    return result.value;
  }

  async saveSerialized(value: SerializedState): Promise<void> {
    await Preferences.set({ key: STORAGE_KEY, value });
  }
}

class SQLiteStorage implements StateStorage {
  private connection: SQLiteDBConnection | null = null;

  private async getConnection(): Promise<SQLiteDBConnection> {
    if (this.connection) return this.connection;

    const sqlite = new SQLiteConnection(CapacitorSQLite);
    await sqlite.checkConnectionsConsistency();

    const existing = await sqlite.isConnection("aus_property_db", false);
    const database = existing.result
      ? await sqlite.retrieveConnection("aus_property_db", false)
      : await sqlite.createConnection(
          "aus_property_db",
          false,
          "no-encryption",
          1,
          false
        );

    await database.open();
    await database.execute(
      "CREATE TABLE IF NOT EXISTS app_state (id TEXT PRIMARY KEY NOT NULL, payload TEXT NOT NULL);"
    );
    this.connection = database;
    return database;
  }

  async loadSerialized(): Promise<SerializedState | null> {
    const db = await this.getConnection();
    const result = await db.query(
      "SELECT payload FROM app_state WHERE id = ?",
      [STORAGE_KEY]
    );
    const row = result.values?.[0];

    if (!row || typeof row !== "object") return null;

    const payload = Reflect.get(row, "payload");
    return typeof payload === "string" ? payload : null;
  }

  async saveSerialized(value: SerializedState): Promise<void> {
    const db = await this.getConnection();
    await db.run(
      "INSERT OR REPLACE INTO app_state (id, payload) VALUES (?, ?)",
      [STORAGE_KEY, value]
    );
  }
}

const createDriver = (): StateStorage => {
  if (Capacitor.isNativePlatform()) {
    return new SQLiteStorage();
  }
  return new PreferencesStorage();
};

const parseState = (payload: SerializedState | null): PersistedState => {
  if (!payload) return defaultPersistedState();

  try {
    const parsed = JSON.parse(payload) as unknown;
    return migrateState(parsed);
  } catch {
    return defaultPersistedState();
  }
};

export class AppRepository {
  private readonly driver = createDriver();

  async load(): Promise<PersistedState> {
    const payload = await this.driver.loadSerialized();
    return parseState(payload);
  }

  async save(state: PersistedState): Promise<void> {
    await this.driver.saveSerialized(JSON.stringify(state));
  }
}
